// React / 기본 라이브러리
import { useEffect, useRef, useState, useCallback } from "react";

// OpenLayers 라이브러리
import { GeoJSON } from "ol/format";
import { Map as OlMap, View, Feature as OlFeature } from "ol";
import { Layer, Vector as VectorLayer } from "ol/layer";
import { Vector as VectorSource, Cluster as ClusterSource } from "ol/source";
import { transformExtent } from "ol/proj";
import {
    getProjection,
    createBaseLayer,
    fitToExtent,
    addPointStyle,
    LayerName,
    createVectorLayer,
    createClusterLayer,
    clusterStyleFunction,
    revealedStyleFunction,
    selectedClusterStyleFunction,
} from "../../module/map/olmap";

import Hover from "ol-ext/interaction/Hover";
import ClusterLayer from "ol-ext/layer/AnimatedCluster";
import SelectCluster from "ol-ext/interaction/SelectCluster";

import { Point } from "ol/geom";
import { singleClick } from "ol/events/condition";
import { CollectionEvent } from "ol/Collection";

import { FeatureCollection } from "geojson";

// 공통 UI 컴포넌트
import { CategoryOptions, Color, findCategoryOption, Metadata } from "../common/Common";

// 스타일
import styles from "./Map.module.css";

interface PreviewMapProps {
    mapType: string;
    geojson: string;
    categories: CategoryOptions[];
    selectedCategory: number;
    metadata?: Metadata;
}

const PreviewMap = ({ mapType, geojson, categories, selectedCategory, metadata }: PreviewMapProps) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapObjRef = useRef<OlMap>();

    const resolutions = [768.75, 522.24, 261.12, 130.56, 65.28, 32.64, 16.32, 8.16, 4.08, 2.04, 1.02, 0.51, 0.255];
    const extent = [90112, 1392896, 1600673, 2161664]; // EPSG:5179 기준

    useEffect(() => {
        if (!mapRef.current) return;

        const mapProj = getProjection(mapType);
        const mapExtent = transformExtent(extent, "EPSG:5179", mapProj);

        // 기본 배경 레이어 및 클러스터 레이어 초기화
        const baseLayer = createBaseLayer(mapType);
        const featureLayer = createVectorLayer(LayerName.FEATURE) as VectorLayer<VectorSource>;
        const clusterLayer = createClusterLayer(LayerName.CLUSTER) as ClusterLayer;

        if (!featureLayer || !clusterLayer) return;

        const vectorSource = featureLayer.getSource() as VectorSource;
        const source = clusterLayer.get("source") as ClusterSource;

        clusterLayer.setStyle(clusterStyleFunction);
        clusterLayer.set("featureStyle", addPointStyle);
        // clusterLayer.setVisible(true);

        const mapObj = new OlMap({
            layers: [baseLayer, featureLayer, clusterLayer],
            target: mapRef.current,
            view: new View({
                projection: mapProj,
                resolutions: resolutions,
                center: [(mapExtent[0] + mapExtent[2]) / 2, (mapExtent[1] + mapExtent[3]) / 2],
                zoom: 0,
                extent: mapExtent,
            }),
            controls: [],
        });

        mapObj.setTarget(mapRef.current);
        mapObjRef.current = mapObj;

        // geojson이 준비되지 않았거나 빈 문자열이면 파싱 시도하지 않음
        if (!geojson || typeof geojson !== "string" || geojson.trim() === "") {
        } else {
            try {
                const parsed: FeatureCollection = JSON.parse(geojson);

                const features = new GeoJSON().readFeatures(parsed, {
                    dataProjection: "EPSG:5179",
                    featureProjection: mapProj,
                }) as OlFeature[];

                // 251125 | smyun | 카테고리 없음(선택안함) 일 경우 마커 처리 추가
                // metadata 존재 여부 및 '카테고리 없음' 모드 판정
                const metaProvided = !!metadata;
                const noCategoryMode = metaProvided && metadata!.category === null;

                // 1) category/label 키 채우기: metadata 우선, 없으면 자동 감지
                if (metaProvided && metadata!.category !== null && metadata!.category !== undefined) {
                    const catKey = metadata!.category; // ex: 'C' (A~Z) _카테고리로 사용할 필드 열
                    const lblKey = metadata!.placeName; // ex: 'B' _ placeName으로 사용할 필드 열
                    features.forEach((feature) => {
                        if (catKey && feature.get(catKey) !== undefined) {
                            const v = feature.get(catKey);
                            feature.set("category", typeof v === "string" ? v.trim() : v);
                        }
                        if (lblKey && feature.get(lblKey) !== undefined) {
                            const v = feature.get(lblKey);
                            feature.set("label", typeof v === "string" ? v.trim() : v);
                        }
                    });
                } else {
                    const { catKey, lblKey } = detectKeys(features);
                    features.forEach((feature) => {
                        if (!feature.get("category") && catKey) {
                            const v = feature.get(catKey);
                            feature.set("category", typeof v === "string" ? v.trim() : v);
                        }
                        if (!feature.get("label") && lblKey) {
                            const v = feature.get(lblKey);
                            feature.set("label", typeof v === "string" ? v.trim() : v);
                        }
                    });
                }

                // 2) 스타일 적용 분기
                if (noCategoryMode) {
                    // 카테고리 없음: metadata.marker/markerColor 를 일괄 적용
                    const icon = metadata!.marker ?? "marker";
                    const color = metadata!.markerColor ?? Color.MAIN;
                    features.forEach((feature) => {
                        feature.setProperties({ icon, color });
                    });
                } else {
                    // 카테고리 모드: 다양한 매칭 방법으로 categories에서 option을 찾아 적용
                    // build label->option map for fast lookup (normalized)
                    const labelMap = new Map<string, (typeof categories)[0]>();
                    for (const c of categories) {
                        const lbl = String(c.label ?? "")
                            .trim()
                            .toLowerCase();
                        if (lbl) labelMap.set(lbl, c);
                    }

                    features.forEach((feature) => {
                        // feature 속성 전체를 스캔해서 categories 라벨과 일치하는 값이 있는지 확인
                        const props = feature.getProperties();
                        let option = undefined;

                        for (const k of Object.keys(props)) {
                            const val = props[k];
                            if (typeof val !== "string") continue;
                            const vnorm = val.trim().toLowerCase();
                            const found = labelMap.get(vnorm); // labelMap은 '정규화된 라벨(lowercase, trim) -> option' 매핑이어야 함
                            if (found) {
                                option = found;
                                break;
                            }
                        }

                        // 아이콘 option 마커에 적용
                        const icon = option?.icon ?? "marker";
                        const color = option?.color ?? Color.BASIC_MARKER;
                        feature.setProperties({ icon, color });
                    });
                }

                // 3) 필터링: 카테고리 없음이면 selectedCategory 무시
                const categoryList =
                    categories.length > 0 && categories[0].label !== "전체" ? [{ label: "전체", disabled: false }, ...categories] : categories;

                // 정규화된 라벨 배열을 만들어 인덱스/비교에 사용
                const normalizedCategoryLabels = categoryList.map((c) => String(c.label ?? "").trim());

                let filtered: OlFeature[] = [];
                if (noCategoryMode) {
                    filtered = features;
                } else {
                    const visibleLabel = normalizedCategoryLabels[selectedCategory] ?? "";
                    if (selectedCategory === 0) {
                        filtered = features;
                    } else {
                        filtered = features.filter((f) => {
                            const catVal = String(f.get("category") ?? "").trim();
                            return catVal === visibleLabel;

                            // 더 안정적으로 (공백/대소문자 무시):
                            // return catVal.toLowerCase() === visibleLabel.toLowerCase();
                        });
                    }
                }

                // 클러스터 소스 연결
                vectorSource.clear(true);
                vectorSource.addFeatures(filtered);
                source.setSource(vectorSource);

                if (filtered.length > 0) {
                    clusterLayer.set("featureStyle", addPointStyle);
                }

                const extent = vectorSource.getExtent();

                if (filtered.length === 1) {
                    mapObj.getView().animate({
                        center: (filtered[0].getGeometry() as Point).getCoordinates(),
                        zoom: 7,
                        duration: 500,
                    });
                } else if (filtered.length > 1 && extent.every((v) => !isNaN(v))) {
                    fitToExtent(mapObj, extent);
                }
            } catch (error) {
                console.error("@@ [PreviewMap] GeoJSON 파싱 오류:", error, "raw:", geojson?.slice?.(0, 200));
            }
        }

        const [select, hover] = [
            new SelectCluster({
                layers: [clusterLayer],
                condition: singleClick,
                pointRadius: 30,
                featureStyle: revealedStyleFunction,
                style: selectedClusterStyleFunction,
                spiral: true,
            }),
            new Hover({
                cursor: "pointer",
                layerFilter: (l: any) => l === clusterLayer,
                featureFilter: (f) => f.getGeometry()?.getType() === "Point",
                handleEvent: () => true,
            }),
        ];

        select.getFeatures().on("add", (e) => onSelectCluster(e, clusterLayer));
        mapObj.addInteraction(select);
        mapObj.addInteraction(hover);

        return () => {
            mapObj.setTarget(undefined);
        };
    }, [mapType, geojson, selectedCategory, metadata]);

    const onSelectCluster = useCallback((e: CollectionEvent<OlFeature>, layer: ClusterLayer) => {
        const cluster = e.element as OlFeature;
        const selectedFeatures = cluster.get("features") as OlFeature[];

        const clusters = layer.getSource()?.getFeatures() ?? [];
        clusters.forEach((clust) => {
            const inner = clust.get("features") as OlFeature[];
            inner?.forEach((f) => f.unset("selected"));
        });

        if (selectedFeatures.length === 1) {
            const feature = selectedFeatures[0];
            feature.set("selected", true);
            // console.log("[PreviewMap] selected feature: ", feature.getProperties());
        }
    }, []);

    const detectKeys = (features: OlFeature[]): { catKey?: string; lblKey?: string } => {
        if (!features.length) return {};
        const sample = features[0].getProperties();
        const catKey = Object.keys(sample).find((k) => categories.some((c) => c.label === sample[k]));
        const lblKey = Object.keys(sample).find((k) => k !== catKey && k !== "geometry" && typeof sample[k] === "string");
        return { catKey, lblKey };
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.map} ref={mapRef}></div>
        </div>
    );
};

export default PreviewMap;
